/**
 * AI SDK 6 Integration for DapperGPT
 *
 * Unified interface to AI providers using the Vercel AI SDK v6.
 * Uses modern v6 callbacks (onChunk, onFinish, onStepFinish) instead of
 * manual fullStream iteration for reliable streaming.
 *
 * Features:
 * - Multi-provider support (OpenAI, Anthropic, Google, xAI, DeepSeek, GLM, Kimi, Qwen)
 * - Streaming via onChunk/onFinish callbacks (v6 best practice)
 * - Non-streaming via generateText
 * - Tool calling with automatic multi-step execution
 * - Reasoning/thinking token capture for all providers
 * - Multipart message support (text + images) for vision models
 *
 * Supported providers:
 * - OpenAI (GPT-5.6 family, GPT-5.x, GPT-4.x, o-series)
 * - Anthropic (Claude 5 family, Claude 4.x)
 * - Google (Gemini 3.x, Gemini 2.5)
 * - xAI (Grok 4.5)
 * - DeepSeek (V4) via OpenAI-compatible
 * - Z.AI GLM, Moonshot Kimi, Alibaba Qwen — all via OpenAI-compatible
 */

import { createOpenAI } from '@ai-sdk/openai';
import { createAnthropic } from '@ai-sdk/anthropic';
import { createGoogleGenerativeAI } from '@ai-sdk/google';
import { createXai } from '@ai-sdk/xai';
import { streamText, generateText, stepCountIs, type LanguageModel, type Tool } from 'ai';
import type { AIApiKeys } from './ai';
import {
    AI_PROVIDERS,
    getModelProvider,
    getModelConfig,
    isModelReasoning,
    supportsSampling,
    getThinkingMode,
    getDefaultEffort,
    clampAnthropicEffort,
    type AIProvider,
    type ChatModel,
} from '../constant';
import type { GPTChatMessage, GPTChatMessageContentImage, OnMessageCallback, OnMessageExtras } from './openai';

// =============================================================================
// TYPES
// =============================================================================

export interface ToolCallResult {
    toolName: string;
    args: unknown;
    result: unknown;
}

// =============================================================================
// PROVIDER FACTORY
// =============================================================================

/**
 * Creates an OpenAI provider instance
 */
export const createOpenAIProvider = (apiKey: string) => {
    return createOpenAI({
        apiKey,
    });
};

/**
 * Creates an Anthropic provider instance
 */
export const createAnthropicProvider = (apiKey: string) => {
    return createAnthropic({
        apiKey,
    });
};

/**
 * Creates a Google Generative AI provider instance
 */
export const createGoogleProvider = (apiKey: string) => {
    return createGoogleGenerativeAI({
        apiKey,
    });
};

/**
 * Creates an xAI provider instance
 */
export const createXaiProvider = (apiKey: string) => {
    return createXai({
        apiKey,
    });
};

/**
 * Creates a DeepSeek provider using OpenAI-compatible interface
 */
export const createDeepSeekProvider = (apiKey: string) => {
    return createOpenAI({
        apiKey,
        baseURL: 'https://api.deepseek.com/v1',
    });
};

/**
 * Z.AI (GLM) — OpenAI-compatible chat completions endpoint.
 */
export const createGLMProvider = (apiKey: string) => {
    return createOpenAI({
        apiKey,
        baseURL: AI_PROVIDERS.glm.baseUrl,
    });
};

/**
 * Moonshot (Kimi) — OpenAI-compatible.
 */
export const createKimiProvider = (apiKey: string) => {
    return createOpenAI({
        apiKey,
        baseURL: AI_PROVIDERS.kimi.baseUrl,
    });
};

/**
 * Alibaba Model Studio (Qwen) — OpenAI-compatible endpoint.
 * Uses the international host; swap to dashscope.aliyuncs.com for mainland China.
 */
export const createQwenProvider = (apiKey: string) => {
    return createOpenAI({
        apiKey,
        baseURL: AI_PROVIDERS.qwen.baseUrl,
    });
};

// =============================================================================
// MODEL FACTORY
// =============================================================================

/**
 * Get the appropriate language model for a given model ID
 */
export const getLanguageModel = (
    modelId: ChatModel,
    apiKeys: AIApiKeys
): LanguageModel => {
    const providerType = getModelProvider(modelId);

    switch (providerType) {
        case 'anthropic': {
            const key = apiKeys.anthropic;
            if (!key) throw new Error('Anthropic API key not configured');
            const provider = createAnthropicProvider(key);
            return provider(modelId) as LanguageModel;
        }

        case 'google': {
            const key = apiKeys.google;
            if (!key) throw new Error('Google API key not configured');
            const provider = createGoogleProvider(key);
            return provider(modelId) as LanguageModel;
        }

        case 'xai': {
            const key = apiKeys.xai;
            if (!key) throw new Error('xAI API key not configured');
            const provider = createXaiProvider(key);
            return provider(modelId) as LanguageModel;
        }

        case 'deepseek': {
            const key = apiKeys.deepseek;
            if (!key) throw new Error('DeepSeek API key not configured');
            const provider = createDeepSeekProvider(key);
            return provider(modelId) as LanguageModel;
        }

        case 'glm': {
            const key = apiKeys.glm;
            if (!key) throw new Error('Z.AI (GLM) API key not configured');
            const provider = createGLMProvider(key);
            return provider(modelId) as LanguageModel;
        }

        case 'kimi': {
            const key = apiKeys.kimi;
            if (!key) throw new Error('Moonshot (Kimi) API key not configured');
            const provider = createKimiProvider(key);
            return provider(modelId) as LanguageModel;
        }

        case 'qwen': {
            const key = apiKeys.qwen;
            if (!key) throw new Error('Alibaba (Qwen) API key not configured');
            const provider = createQwenProvider(key);
            return provider(modelId) as LanguageModel;
        }

        case 'openai':
        default: {
            const key = apiKeys.openai;
            if (!key) throw new Error('OpenAI API key not configured');
            const provider = createOpenAIProvider(key);
            return provider(modelId) as LanguageModel;
        }
    }
};

// =============================================================================
// REASONING / THINKING OPTIONS
// =============================================================================

/**
 * Build the `providerOptions` payload that enables reasoning for a model.
 *
 * Each provider expresses "think before answering" differently, and getting this
 * wrong is a hard 400 rather than a silent degradation:
 *
 * - Anthropic 'adaptive' (Opus 5, Sonnet 5, Fable 5, Opus 4.7/4.8, 4.6):
 *   the `thinking` option must be OMITTED. Adaptive thinking is the server-side
 *   default, and sending `{ type: 'enabled', budgetTokens }` returns a 400 on
 *   Opus 5 / Sonnet 5 / Fable 5 / Opus 4.7 / 4.8. Depth is steered with `effort`.
 * - Anthropic 'budget' (Claude 3.7 – 4.5): the legacy explicit budget, which must
 *   be strictly less than maxOutputTokens.
 * - OpenAI: `reasoningEffort` + `reasoningSummary`.
 * - Google: `thinkingConfig.includeThoughts`.
 * - xAI: `reasoningEffort` under the `xai` key (NOT `openai` — a previous version
 *   of this file wrote xAI and DeepSeek options under `openai`, where the xAI
 *   provider never reads them).
 * - DeepSeek: served through the OpenAI-compatible provider, so `openai` is correct.
 */
const buildReasoningProviderOptions = (
    modelId: ChatModel,
    maxOutputTokens?: number
): Record<string, Record<string, unknown>> | undefined => {
    if (!isModelReasoning(modelId)) return undefined;

    const provider = getModelProvider(modelId);
    const mode = getThinkingMode(modelId);
    const effort = getDefaultEffort(modelId);
    const opts: Record<string, Record<string, unknown>> = {};

    switch (provider) {
        case 'anthropic': {
            const anthropic: Record<string, unknown> = {};
            if (mode === 'budget') {
                // budgetTokens must stay below the output cap, and the Anthropic
                // API requires a minimum of 1024.
                const cap = maxOutputTokens ?? getModelConfig(modelId)?.maxOutputTokens ?? 16000;
                const budget = Math.max(1024, Math.min(10000, cap - 1024));
                if (budget >= 1024 && budget < cap) {
                    anthropic.thinking = { type: 'enabled', budgetTokens: budget };
                }
            }
            // For 'adaptive' we deliberately send no `thinking` key at all.
            const anthropicEffort = clampAnthropicEffort(effort);
            if (anthropicEffort) anthropic.effort = anthropicEffort;
            if (Object.keys(anthropic).length > 0) opts.anthropic = anthropic;
            break;
        }

        case 'google':
            opts.google = { thinkingConfig: { includeThoughts: true } };
            break;

        case 'xai':
            opts.xai = { reasoningEffort: effort ?? 'high' };
            break;

        case 'deepseek':
            // DeepSeek runs through the OpenAI-compatible provider.
            opts.openai = { reasoningSummary: 'detailed' };
            break;

        case 'glm':
        case 'qwen':
            // Both are served through the OpenAI-compatible provider, so their
            // options ride under the `openai` key.
            opts.openai = { ...(effort ? { reasoningEffort: effort } : {}) };
            break;

        case 'kimi':
            // Kimi K3 always reasons; `reasoning_effort` accepts low | high | max
            // (no "medium"), so map anything mid-range up to high.
            opts.openai = {
                reasoningEffort: effort === 'low' ? 'low' : effort === 'xhigh' ? 'max' : 'high',
            };
            break;

        case 'openai':
        default:
            opts.openai = {
                reasoningSummary: 'detailed',
                ...(effort ? { reasoningEffort: effort } : {}),
            };
            break;
    }

    return Object.keys(opts).length > 0 ? opts : undefined;
};

// =============================================================================
// MESSAGE CONVERSION
// =============================================================================

type SDKContentPart =
    | { type: 'text'; text: string }
    | { type: 'image'; image: URL };

type SDKMessageContent = string | SDKContentPart[];

type SDKMessageMultipart = {
    role: 'system' | 'user' | 'assistant';
    content: SDKMessageContent;
};

/**
 * Convert internal message format to AI SDK v6 message format.
 * Properly handles multipart content (text + images) for vision models.
 */
export const convertToSDKMessages = (messages: GPTChatMessage[]): SDKMessageMultipart[] => {
    return messages.map((msg): SDKMessageMultipart => {
        // Map role first
        const role: 'system' | 'user' | 'assistant' =
            msg.role === 'system' || msg.role === 'developer' ? 'system'
                : msg.role === 'assistant' ? 'assistant'
                    : 'user';

        // Handle string content (most common)
        if (typeof msg.content === 'string') {
            return { role, content: msg.content };
        }

        // Handle array content (multipart: text + images)
        if (Array.isArray(msg.content)) {
            const parts: SDKContentPart[] = [];
            for (const part of msg.content) {
                if (part.type === 'text') {
                    parts.push({ type: 'text', text: (part as { type: 'text'; text: string }).text });
                } else if (part.type === 'image_url') {
                    const imageUrl = (part as GPTChatMessageContentImage).image_url?.url;
                    if (imageUrl) {
                        parts.push({ type: 'image', image: new URL(imageUrl) });
                    }
                }
            }
            // If only text parts, collapse to a single string for efficiency
            if (parts.every(p => p.type === 'text')) {
                return { role, content: parts.map(p => (p as { type: 'text'; text: string }).text).join('\n') };
            }
            return { role, content: parts };
        }

        // Fallback
        return { role, content: String(msg.content) };
    });
};

// =============================================================================
// STREAMING CHAT
// =============================================================================

export interface AISDKStreamOptions {
    onMessage?: OnMessageCallback;
    temperature?: number;
    maxTokens?: number;
    signal?: AbortSignal;
    system?: string;
    // Tool calling options
    tools?: Record<string, Tool>;
    toolChoice?: 'auto' | 'none' | 'required';
    onToolCall?: (toolName: string, args: unknown) => void;
    onToolResult?: (toolName: string, result: unknown) => void;
    maxSteps?: number;
}

export interface AISDKResult {
    text: string;
    reasoning?: string;
    usage?: {
        promptTokens: number;
        completionTokens: number;
    };
    toolCalls?: ToolCallResult[];
}

/**
 * Stream a chat completion using AI SDK v6.
 *
 * Uses the modern onChunk/onFinish/onStepFinish callback pattern
 * (recommended by AI SDK v6) instead of manually iterating fullStream.
 * This avoids the text-delta/.delta property confusion and is more reliable.
 *
 * Supports tool calling with automatic execution and multi-step processing.
 * Captures reasoning/thinking tokens separately from response text.
 */
export const streamChatWithAISDK = async (
    modelId: ChatModel,
    messages: GPTChatMessage[],
    apiKeys: AIApiKeys,
    options: AISDKStreamOptions = {}
): Promise<AISDKResult> => {
    const model = getLanguageModel(modelId, apiKeys);
    const sdkMessages = convertToSDKMessages(messages);
    const modelConfig = getModelConfig(modelId);

    // Sampling params are a 400 on Claude Opus 5 / Sonnet 5 / Fable 5 / Opus 4.7 /
    // 4.8 and the OpenAI reasoning models — omit the key entirely for those.
    const temperature = supportsSampling(modelId) ? (options.temperature ?? 0.7) : undefined;

    // Mutable state accumulated via callbacks
    let fullContent = '';
    let fullReasoning = '';
    let firstCall = true;
    let isCurrentlyThinking = false;
    const toolCallResults: ToolCallResult[] = [];

    // Resolved by onFinish
    let resolveFinish: (value: AISDKResult) => void;
    let rejectFinish: (error: Error) => void;
    const finishPromise = new Promise<AISDKResult>((resolve, reject) => {
        resolveFinish = resolve;
        rejectFinish = reject;
    });

    try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const streamOptions: any = {
            model,
            messages: sdkMessages,
            ...(temperature !== undefined && { temperature }),
            maxOutputTokens: options.maxTokens,
            abortSignal: options.signal,
            ...(options.system && { system: options.system }),

            // ─── Modern v6 callback: per-chunk processing ───
            onChunk({ chunk }: { chunk: { type: string; text?: string; id?: string } }) {
                switch (chunk.type) {
                    case 'text-delta': {
                        // AI SDK v6: onChunk receives 'text-delta' with .text property
                        fullContent += chunk.text;
                        if (isCurrentlyThinking) {
                            isCurrentlyThinking = false;
                        }
                        options.onMessage?.(fullContent, firstCall, false, {
                            reasoning: fullReasoning || undefined,
                            isThinking: false,
                        });
                        firstCall = false;
                        break;
                    }
                    case 'reasoning-delta': {
                        // AI SDK v6: reasoning tokens arrive as 'reasoning-delta' with .text
                        fullReasoning += chunk.text;
                        if (!isCurrentlyThinking) {
                            isCurrentlyThinking = true;
                        }
                        options.onMessage?.(fullContent, firstCall, false, {
                            reasoning: fullReasoning,
                            isThinking: true,
                        });
                        firstCall = false;
                        break;
                    }
                    // 'source', 'tool-call', 'tool-input-start', etc. — handled by onStepFinish
                    default:
                        break;
                }
            },

            // ─── Modern v6 callback: stream finished ───
            onFinish({ text, reasoning, usage }: {
                text: string;
                reasoning?: { text: string }[];
                usage?: { inputTokens?: number; outputTokens?: number };
                finishReason?: string;
            }) {
                // Signal last call to the UI
                options.onMessage?.('', false, true, {
                    reasoning: fullReasoning || undefined,
                    isThinking: false,
                });

                resolveFinish({
                    text: text || fullContent,
                    reasoning: reasoning?.map(r => r.text).join('') || fullReasoning || undefined,
                    usage: usage ? {
                        promptTokens: usage.inputTokens ?? 0,
                        completionTokens: usage.outputTokens ?? 0,
                    } : undefined,
                    toolCalls: toolCallResults.length > 0 ? toolCallResults : undefined,
                });
            },

            // ─── Error callback ───
            onError({ error }: { error: unknown }) {
                const err = error instanceof Error ? error : new Error(String(error));
                rejectFinish(err);
            },
        };

        // Add provider-specific options for reasoning/thinking
        const providerOpts = buildReasoningProviderOptions(modelId, options.maxTokens);
        if (providerOpts) {
            streamOptions.providerOptions = providerOpts;
        }

        // Add tools if provided
        if (options.tools && Object.keys(options.tools).length > 0) {
            streamOptions.tools = options.tools;
            streamOptions.toolChoice = options.toolChoice || 'auto';
            streamOptions.stopWhen = stepCountIs(options.maxSteps || 5);

            streamOptions.onStepFinish = async ({ toolCalls, toolResults }: {
                toolCalls?: { toolCallId: string; toolName: string; input: unknown }[];
                toolResults?: { toolCallId: string; output: unknown }[];
            }) => {
                if (toolCalls && toolResults) {
                    for (const result of toolResults) {
                        const toolCall = toolCalls.find((tc: { toolCallId: string }) => tc.toolCallId === result.toolCallId);
                        if (toolCall) {
                            toolCallResults.push({
                                toolName: toolCall.toolName,
                                args: toolCall.input,
                                result: result.output,
                            });
                            options.onToolCall?.(toolCall.toolName, toolCall.input);
                            options.onToolResult?.(toolCall.toolName, result.output);
                        }
                    }
                }
            };
        }

        // Start streaming — the callbacks above handle everything
        const result = streamText(streamOptions);

        // Consume the stream (drives the callbacks). We don't need to read
        // individual chunks because onChunk/onFinish do all the work.
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        for await (const _part of result.fullStream) {
            // Intentionally empty — stream must be consumed to trigger callbacks
        }

        // Wait for onFinish to resolve with the complete result
        return await finishPromise;

    } catch (error) {
        const err = error as Error;
        const message = err?.message || 'Unknown error occurred';

        if (message.includes('rate limit') || message.includes('429')) {
            throw new Error('Rate limit exceeded. Please wait a moment and try again.');
        }

        if (message.includes('context') || message.includes('tokens')) {
            throw new Error(`Message too long for ${modelConfig?.displayName || modelId}. Try using a shorter message.`);
        }

        if (message.includes('api key') || message.includes('authentication') || message.includes('401')) {
            throw new Error('Invalid API key. Please check your API key and try again.');
        }

        throw new Error(message);
    }
};

// =============================================================================
// NON-STREAMING CHAT
// =============================================================================

export interface AISDKGenerateOptions {
    temperature?: number;
    maxTokens?: number;
    signal?: AbortSignal;
    system?: string;
    // Tool calling options
    tools?: Record<string, Tool>;
    toolChoice?: 'auto' | 'none' | 'required';
    maxSteps?: number;
}

/**
 * Generate a chat completion without streaming using AI SDK v6.
 *
 * Uses generateText which returns the full result after completion.
 * Supports tool calling with automatic multi-step execution.
 */
export const generateChatWithAISDK = async (
    modelId: ChatModel,
    messages: GPTChatMessage[],
    apiKeys: AIApiKeys,
    options: AISDKGenerateOptions = {}
): Promise<AISDKResult> => {
    const model = getLanguageModel(modelId, apiKeys);
    const sdkMessages = convertToSDKMessages(messages);
    const modelConfig = getModelConfig(modelId);

    // Sampling params are a 400 on the newer reasoning models — omit entirely.
    const temperature = supportsSampling(modelId) ? (options.temperature ?? 0.7) : undefined;

    try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const generateOptions: any = {
            model,
            messages: sdkMessages,
            ...(temperature !== undefined && { temperature }),
            maxOutputTokens: options.maxTokens,
            abortSignal: options.signal,
            ...(options.system && { system: options.system }),
        };

        // Enable reasoning/thinking for reasoning-capable models
        const providerOpts = buildReasoningProviderOptions(modelId, options.maxTokens);
        if (providerOpts) {
            generateOptions.providerOptions = providerOpts;
        }

        // Add tools if provided
        if (options.tools && Object.keys(options.tools).length > 0) {
            generateOptions.tools = options.tools;
            generateOptions.toolChoice = options.toolChoice || 'auto';
            generateOptions.stopWhen = stepCountIs(options.maxSteps || 5);
        }

        const result = await generateText(generateOptions);

        // Collect tool calls from steps (v6 uses .input and .output)
        const toolCallResults: ToolCallResult[] = [];
        if (result.steps) {
            for (const step of result.steps) {
                if (step.toolCalls && step.toolResults) {
                    for (const toolCall of step.toolCalls) {
                        const toolResult = step.toolResults.find(
                            (tr: { toolCallId: string }) => tr.toolCallId === toolCall.toolCallId
                        );
                        if (toolResult) {
                            toolCallResults.push({
                                toolName: toolCall.toolName,
                                args: toolCall.input,
                                result: (toolResult as { output: unknown }).output,
                            });
                        }
                    }
                }
            }
        }

        // v6: result.reasoning is an array of reasoning parts, result.reasoningText is the joined string
        const reasoningText = (result as { reasoningText?: string }).reasoningText
            || (result as { reasoning?: { text: string }[] }).reasoning?.map(r => r.text).join('')
            || undefined;

        return {
            text: result.text,
            reasoning: reasoningText,
            usage: result.usage ? {
                promptTokens: result.usage.inputTokens ?? 0,
                completionTokens: result.usage.outputTokens ?? 0,
            } : undefined,
            toolCalls: toolCallResults.length > 0 ? toolCallResults : undefined,
        };
    } catch (error) {
        const err = error as Error;
        const message = err?.message || 'Unknown error occurred';
        throw new Error(message);
    }
};

// =============================================================================
// UNIFIED HANDLER
// =============================================================================

export interface AISDKHandlerOptions {
    onMessage?: OnMessageCallback;
    useStreaming?: boolean;
    temperature?: number;
    maxTokens?: number;
    // Tool calling
    tools?: Record<string, Tool>;
    toolChoice?: 'auto' | 'none' | 'required';
    onToolCall?: (toolName: string, args: unknown) => void;
    onToolResult?: (toolName: string, result: unknown) => void;
    maxSteps?: number;
}

export interface AISDKHandlerResult {
    content: string;
    toolCalls?: ToolCallResult[];
}

/**
 * Unified AI chat handler using AI SDK 6
 * Drop-in replacement for aiChatRequestHandler
 * 
 * @example
 * // Basic usage
 * const result = await aiSDKChatHandler(apiKeys, 'gpt-4o', messages);
 * 
 * @example
 * // With streaming
 * await aiSDKChatHandler(apiKeys, 'claude-sonnet-4-5', messages, signal, {
 *   useStreaming: true,
 *   onMessage: (text) => console.log(text),
 * });
 * 
 * @example
 * // With tools
 * import { availableTools } from './ai-tools';
 * const result = await aiSDKChatHandler(apiKeys, 'gpt-4o', messages, signal, {
 *   tools: { calculator: availableTools.calculator },
 *   onToolCall: (name, args) => console.log(`Calling ${name}`, args),
 * });
 */
export async function aiSDKChatHandler(
    apiKeys: AIApiKeys,
    model: ChatModel,
    messages: GPTChatMessage[],
    signal?: AbortController,
    options?: AISDKHandlerOptions & { useStreaming: true }
): Promise<null>;
export async function aiSDKChatHandler(
    apiKeys: AIApiKeys,
    model: ChatModel,
    messages: GPTChatMessage[],
    signal?: AbortController,
    options?: AISDKHandlerOptions
): Promise<AISDKHandlerResult>;
export async function aiSDKChatHandler(
    apiKeys: AIApiKeys,
    model: ChatModel,
    messages: GPTChatMessage[],
    signal?: AbortController,
    options?: AISDKHandlerOptions
): Promise<AISDKHandlerResult | null> {
    if (options?.useStreaming && options?.onMessage) {
        await streamChatWithAISDK(model, messages, apiKeys, {
            onMessage: options.onMessage,
            temperature: options.temperature,
            maxTokens: options.maxTokens,
            signal: signal?.signal,
            tools: options.tools,
            toolChoice: options.toolChoice,
            onToolCall: options.onToolCall,
            onToolResult: options.onToolResult,
            maxSteps: options.maxSteps,
        });
        return null;
    } else {
        const result = await generateChatWithAISDK(model, messages, apiKeys, {
            temperature: options?.temperature,
            maxTokens: options?.maxTokens,
            signal: signal?.signal,
            tools: options?.tools,
            toolChoice: options?.toolChoice,
            maxSteps: options?.maxSteps,
        });
        return {
            content: result.text,
            toolCalls: result.toolCalls,
        };
    }
}
